import * as XLSX from 'xlsx';

// Column mapping configuration - matches Excel headers to JSON fields for SP
export const PROCESSOR_COLUMN_MAPPING = {
    // Business Profile
    'Name of Business': 'businessName',
    'Address': 'address',
    'Contact Number(s)': 'contactNumber',
    'Length of Business Operation in Years': 'lengthOfOperation',
    'Status of Business': 'businessStatus',
    'Type of Business': 'businessType',
    'Name of the Owner': 'ownerName',
    'Owner Address': 'ownerAddress',
    'Initial Investment': 'initialInvestment',
    'Average Annual Gross Income (last 5 years) in Pesos': 'averageAnnualIncome',
    'Number of Employees': 'numberOfEmployees',
    'Employee Type': 'type',

    // Processing
    'Quantity of Cacao Beans per Processing Cycle (kg)': 'quantityPerCycle',
    'Frequency of Processing': 'processingFrequency',
    '4.1 Preferred Variety of Cacao': 'preferredVariety',
    '4.2 Variety of Cacao Processed': 'varietyProcessed',
    '4.3 Source of Cacao': 'sourceCacao',
    '4.4.1 Producers to Processors': 'producersToProcessors',
    '4.4.2 Local Traders to Processors': 'localTradersToProcessors',
    '4.5 Frequency of Delivery (in a month)': 'deliveryFrequency',
    '4.6 Quantity (Kilograms) of Cacao Delivered': 'deliveryQuantity',
    '4.7 Form/Type of Cacao Bought': 'cacaoForm',
    'Form Type': 'formType',

    // Post Harvest
    '4.8 Pod Breaking': 'podBreaking',
    '4.9 Bean Extraction': 'beanExtraction',
    '4.10 Fermentation': 'fermentation',
    'Containment Materials': 'containmentMaterials',
    'Number of Days': 'fermentationDays',
    'Temperature Monitoring': 'temperatureMonitoring', // appears twice but kept same key
    'Preferred fermentation temperature': 'preferredFermentationTemp',

    '4.11 Drying/ReDrying': 'drying',
    'Drying Type': 'dryingType',
    '4.11.1 Preferred Moisture Content': 'preferredMoistureContent',
    'Method of MC Determination': 'mcMethod',

    '4.12 Bean Sorting': 'beanSorting',
    'Usual Bean Size': 'usualBeanSize',

    '4.13 Bean Grading': 'beanGrading',
    'Level of Fermentation (%)': 'fermentationLevel',
    'Raw Bean Aroma': 'rawBeanAroma',

    '4.14 Materials used for dried bean storage': 'storageMaterials',
    'Estimated Cost of Materials': 'storageCost',

    // Processing Techniques
    '4.15 Roasting': 'roasting',
    'Method': 'roastingMethod', // roasting method
    'Average Time Spent': 'roastingTime',
    'Roasting Temperature Monitoring': 'roastingTempMonitoring',

    '4.16 Cracking': 'cracking',
    'Cracking Type': 'crackingType',

    '4.17 Winnowing': 'winnowing',
    'Winnowing Type': 'winnowingType',

    '4.18 Grinding': 'grinding',
    'Grinding Type': 'grindingType',

    '4.19 Conching': 'conching',
    'Preferred Microns Size': 'preferredMicronsSize',

    '4.20 Tempering': 'tempering',
    'Tempering Type': 'temperingType',
    
    '4.21 Moulding': 'moulding',
    'Moulding Type': 'mouldingType',

    '4.22 Storage of Processed Product': 'storageProcessedProduct',
    'Preferred Storage Temperature': 'preferredStorageTemp',
    'Preferred Relative Humidity (RH)': 'preferredRelativeHumidity',

    '4.23 Packaging Material': 'packagingMaterial',

    // Marketing
    '5. Marketing': 'marketing',
    '5.1 Type of Product Sold': 'typeOfProductSold',
    '5.2 Type of Marketing': 'typeOfMarketing',
    '5.3 Market': 'market',
    'Marketplace': 'marketplace',
    'Local Market': 'localMarket',
    'International': 'international',

    // Business Insights
    'What interest you to go on cacao processing (tableya/chocolate)?': 'interestInCacaoProcessing',
    'What are the challenges you encountered in processing tableya/chocolate?': 'challengesEncountered',
    'What are the supports you need to improve your business?': 'supportsNeeded',
    'What are your future plans?': 'futurePlans',

    // Location
    'Latitude': 'latitude',
    'Longitude': 'longitude'
};

export const parseProcessorBulkExcel = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                
                // Convert to JSON with header row
                const rawData = XLSX.utils.sheet_to_json(worksheet, { 
                    header: 1,
                    defval: '',
                    raw: false // Get formatted values as strings
                });

                if (rawData.length < 3) {
                    reject(new Error('Excel file is empty or has insufficient data rows'));
                    return;
                }
                // Find the header row - look for "Name of Business" as indicator
                let headerRowIndex = 0;
                for (let i = 0; i < Math.min(5, rawData.length); i++) {
                    const row = rawData[i];
                    if (row.some(cell => String(cell).includes('Name of Business'))) {
                        headerRowIndex = i;
                        break;
                    }
                }
                // Extract headers and data
                const headers = rawData[headerRowIndex];
                const dataRows = rawData.slice(headerRowIndex + 1);
                // Find all "Type" column positions for debugging
                // Type column positions identified from headers
                // Log which headers are being mapped
                const mappedHeaders = [];
                const unmappedHeaders = [];
                headers.forEach((header, index) => {
                    const headerString = String(header || '').trim();
                    if (PROCESSOR_COLUMN_MAPPING[headerString]) {
                        mappedHeaders.push({ col: index, header: headerString, field: PROCESSOR_COLUMN_MAPPING[headerString] });
                    } else if (headerString) {
                        unmappedHeaders.push({ col: index, header: headerString });
                    }
                });
                // Map data using column mapping
                const processors = dataRows
                    .filter(row => row.some(cell => cell !== '')) // Skip empty rows
                    .map((row, index) => {
                        const processor = {};
                        
                        // Track which duplicate column we're on
                        let temperatureMonitoringCount = 0;
                        let preferredTemperatureCount = 0;
                        
                        headers.forEach((header, colIndex) => {
                            // Convert header to string and handle potential nulls/undefined, trim whitespace
                            const headerString = String(header || '').trim();
                            let fieldName = PROCESSOR_COLUMN_MAPPING[headerString];
                            
                            // Handle duplicate "Temperature Monitoring" columns
                            if (headerString === 'Temperature Monitoring') {
                                temperatureMonitoringCount++;
                                if (temperatureMonitoringCount === 1) {
                                    fieldName = 'temperatureMonitoring'; // Fermentation temp monitoring
                                } else if (temperatureMonitoringCount === 2) {
                                    fieldName = 'roastingTempMonitoring'; // Roasting temp monitoring
                                }
                            }
                            
                            // Handle duplicate "Preferred Temperature" columns
                            if (headerString === 'Preferred Temperature') {
                                preferredTemperatureCount++;
                                if (preferredTemperatureCount === 1) {
                                    fieldName = 'preferredRoastingTemp'; // First occurrence - Roasting temperature
                                } else if (preferredTemperatureCount === 2) {
                                    fieldName = 'preferredTemperingTemp'; // Second occurrence - Tempering temperature
                                }
                            }
                            
                            if (fieldName) {
                                let value = row[colIndex];
                                
                                // Type conversion for numeric fields
                                if ([
                                    'lengthOfOperation', 'initialInvestment', 'averageAnnualIncome',
                                    'numberOfEmployees', 'quantityPerCycle', 'deliveryQuantity',
                                    'fermentationDays', 'preferredFermentationTemp', 'preferredMoistureContent',
                                    'fermentationLevel', 'storageCost', 'preferredMicronsSize',
                                    'latitude', 'longitude'
                                ].includes(fieldName)) {
                                    // Handle comma-separated numbers (e.g., "13,000" -> 13000)
                                    if (typeof value === 'string') {
                                        // Remove commas and parse as float
                                        value = parseFloat(value.replace(/,/g, '')) || 0;
                                    } else {
                                        value = parseFloat(value) || 0;
                                    }
                                }
                                
                                // Boolean conversion
                                if (fieldName === 'temperatureMonitoring' || fieldName === 'roastingTempMonitoring') {
                                    value = ['yes', 'true', '1', 1].includes(String(value).toLowerCase());
                                }
                                
                                processor[fieldName] = value || '';
                            }
                        });

                        // Set defaults and validate required fields
                        processor.businessStatus = processor.businessStatus || 'Active';
                        processor.rowNumber = index + 2; // Excel row number (header is row 1)
                        
                        // Ensure required fields are not empty strings
                        if (!processor.businessName || processor.businessName.trim() === '') {
                        }
                        
                        return processor;
                    });
                // Debug: Check for required fields
                const firstProcessor = processors[0];
                if (firstProcessor) {
                }
                
                // Debug: Check for any undefined values that might cause issues
                processors.forEach((processor, index) => {
                    const undefinedKeys = Object.keys(processor).filter(key => processor[key] === undefined);
                    if (undefinedKeys.length > 0) {
                    }
                });
                
                resolve(processors);
            } catch (error) {
                reject(new Error(`Failed to parse Excel file: ${error.message}`));
            }
        };

        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsArrayBuffer(file);
    });
};

export const generateProcessorTemplate = () => {
    const headers = Object.keys(PROCESSOR_COLUMN_MAPPING);
    const sampleData = [
        [
            'Sample Cacao Processing Inc.', // Name of Business
            'Main Street, Poblacion', // Address
            '09171234567', // Contact Number(s)
            '5', // Length of Business Operation in Years
            'Active', // Status of Business
            'Corporation', // Type of Business
            'Juan Dela Cruz', // Name of the Owner
            'Main Street, Poblacion', // Owner Address
            '100000', // Initial Investment
            '500000', // Average Annual Gross Income (last 5 years) in Pesos
            '10', // Number of Employees
            'Processing', // Employee Type
            '500', // Quantity of Cacao Beans per Processing Cycle (kg)
            'Monthly', // Frequency of Processing
            'Trinitario', // 4.1 Preferred Variety of Cacao
            'Trinitario', // 4.2 Variety of Cacao Processed
            'Local Farmers', // 4.3 Source of Cacao
            'Direct', // 4.4.1 Producers to Processors
            'Through Traders', // 4.4.2 Local Traders to Processors
            '2 times', // 4.5 Frequency of Delivery (in a month)
            '1000', // 4.6 Quantity (Kilograms) of Cacao Delivered
            'Fresh Beans', // 4.7 Form/Type of Cacao Bought
            'Manual', // Form Type
            'Manual', // 4.8 Pod Breaking
            'Manual', // 4.9 Bean Extraction
            'Container', // 4.10 Fermentation
            'Plastic Containers', // Containment Materials
            '3', // Number of Days
            'Yes', // Temperature Monitoring
            '28', // Preferred fermentation temperature
            'Solar', // 4.11 Drying/ReDrying
            'Solar', // Drying Type
            '7', // 4.11.1 Preferred Moisture Content
            'Manual', // Method of MC Determination
            'Visual', // 4.12 Bean Sorting
            'Medium', // Usual Bean Size
            'Visual', // 4.13 Bean Grading
            '80', // Level of Fermentation (%)
            'Fruity', // Raw Bean Aroma
            'Sacks', // 4.14 Materials used for dried bean storage
            '5000', // Estimated Cost of Materials
            'Drum Roaster', // 4.15 Roasting
            'Gas', // Method
            '30 minutes', // Average Time Spent
            '140', // Preferred Temperature
            'Machine', // 4.16 Cracking
            'Mechanical', // Craking Type
            'Fan', // 4.17 Winnowing
            'Electric', // Winnowing Type
            'Stone Grinder', // 4.18 Grinding
            'Manual', // Grinding Type
            'Conche', // 4.19 Conching
            '25', // Preferred Microns Size
            'Manual', // 4.20 Tempering
            'Heat', // Tempering Type
            '32', // Preferred Temperature (with trailing space)
            'Plastic Molds', // 4.21 Moulding
            'Manual', // Moulding Type
            'Room Storage', // 4.22 Storage of Processed Product
            '25', // Preferred Storage Temperature
            '65', // Preferred Relative Humidity (RH)
            'Plastic Bags', // 4.23 Packaging Material
            'Tablea', // 5. Marketing
            'Direct Selling', // 5.1 Type of Product Sold
            'Local', // 5.2 Type of Marketing
            'Public Market', // 5.3 Market
            'Yes', // Marketplace
            'No', // Local Market
            'Interest in tradition', // International
            'Equipment limitations', // What interest you to go on cacao processing (tableya/chocolate)?
            'Better equipment', // What are the challenges you encountered in processing tableya/chocolate?
            'Expand operations', // What are the supports you need to improve your business?
            '9.123456', // What are your future plans?
            '123.456789' // Latitude // Longitude
        ]
    ];

    const ws = XLSX.utils.aoa_to_sheet([headers, ...sampleData]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Processors');
    
    // Auto-size columns
    const wscols = headers.map(() => ({ wch: 20 }));
    ws['!cols'] = wscols;
    
    XLSX.writeFile(wb, 'processor_comprehensive_template.xlsx');
};